import { diatonicChord, diatonicChords, DEGREES, type Degree } from "../../theory/diatonic";
import { transposeKey } from "../../theory/key";
import { voiceDiatonicChord } from "../../theory/diatonic";
import { barTicks } from "../../sequencer/time";
import type { Project } from "../../project/schema";
import { usePosition, useProject, useServices } from "../context";
import { Panel, Select, Slider, Toggle } from "./controls";

type Prog = Project["sequence"]["progression"];

export function ProgressionEditor() {
  const { controller } = useServices();
  const prog = useProject((p) => p.sequence.progression);
  const key = useProject((p) => p.song.key);
  const transpose = useProject((p) => p.song.transposeSemitones);
  const ts = useProject((p) => p.song.timeSignature);
  const pos = usePosition();
  const perfKey = transposeKey(key, transpose);
  const currentBar = pos === null || !prog.enabled ? -1 : Math.floor(pos / barTicks(ts));
  const options = diatonicChords(key).map((c) => ({ value: c.degree, label: `${c.roman} — ${c.symbol}` }));

  const set = (patch: Partial<Prog>) => controller.updateProject((p) => ({ ...p, sequence: { ...p.sequence, progression: { ...p.sequence.progression, ...patch } } }));
  const setChord = (i: number, patch: Partial<Prog["chords"][number]>) => set({ chords: prog.chords.map((c, j) => (j === i ? { ...c, ...patch } : c)) });

  return (
    <Panel
      title="Chord progression" className="span-all"
      actions={<Toggle pressed={prog.enabled} onChange={(enabled) => set({ enabled })} title="Include the progression in playback">{prog.enabled ? "On" : "Off"}</Toggle>}
    >
      <ol className="progression">
        {prog.chords.map((c, i) => {
          const written = diatonicChord(key, c.degree, c.seventh);
          const sounding = diatonicChord(perfKey, c.degree, c.seventh);
          return (
            <li key={i} className={`bar ${i === currentBar ? "playing" : ""}`}>
              <span className="bar-num">Bar {i + 1}</span>
              <Select label={`Chord for bar ${i + 1}`} hideLabel value={c.degree} options={options} onChange={(degree: Degree) => setChord(i, { degree })} />
              <strong className="chord-symbol">{written.symbol}</strong>
              {transpose !== 0 && <span className="sounding" title="Sounding chord after transposition">sounds {sounding.symbol}</span>}
              <div className="bar-actions">
                <Toggle pressed={c.seventh} onChange={(seventh) => setChord(i, { seventh })} label={`Seventh chord, bar ${i + 1}`}>7th</Toggle>
                <button type="button" aria-label={`Preview bar ${i + 1}`} onClick={() => controller.previewNotes(voiceDiatonicChord(key, c.degree, { octave: prog.octave, seventh: c.seventh, inversion: prog.inversion }))}>Hear</button>
                <button type="button" aria-label={`Remove bar ${i + 1}`} disabled={prog.chords.length <= 1} onClick={() => set({ chords: prog.chords.filter((_, j) => j !== i) })}>×</button>
              </div>
            </li>
          );
        })}
      </ol>
      <div className="controls-row">
        <button type="button" disabled={prog.chords.length >= 16} onClick={() => set({ chords: [...prog.chords, { degree: 1, seventh: false }] })}>+ Add bar</button>
        <Select label="Rhythm" value={prog.rhythm} options={[{ value: "whole", label: "Whole bar" }, { value: "half", label: "Halves" }, { value: "quarters", label: "Quarters" }, { value: "eighths", label: "Eighths" }]} onChange={(rhythm) => set({ rhythm })} />
        <Select label="Inversion" value={prog.inversion} options={[0, 1, 2].map((v) => ({ value: v, label: ["Root position", "1st inversion", "2nd inversion"][v]! }))} onChange={(inversion) => set({ inversion })} />
        <Slider label="Octave" value={prog.octave} min={2} max={5} step={1} format={(v) => String(v)} onChange={(octave) => set({ octave })} />
        <Toggle pressed={prog.bassNote} onChange={(bassNote) => set({ bassNote })} title="Double the root an octave below">Bass note</Toggle>
      </div>
      <p className="hint">Chords are stored as scale degrees ({DEGREES.length} per key), so changing the key re-spells them; transposition shifts what you hear.</p>
    </Panel>
  );
}
